import { useProductAddContext } from './ProductsAddOutlet';
import { ProductsConfigurationReservation } from './ProductsConfigurationReservation';
import { ProductsConfigurationSubscription } from './ProductsConfigurationSubscription';
import { ParkingaboZoneInfo } from '../../../shared/ParkingaboProductModels';
import { exhaustiveGuard } from 'dg-web-shared/lib/ExhaustiveGuard';
import { ProductsConfigurationGuestReservation } from './ProductsConfigurationGuestReservation';
import { ParkingaboOverlineList } from '../../../components/layout/ParkingaboOverlineList';
import { Localized } from 'dg-web-shared/lib/Localization';
import { Box, Stack } from '@mui/material';
import { VehicleLicensePlateType } from 'dg-web-shared/common/models/Vehicle';
import DriveEta from '@mui/icons-material/DriveEta';
import TwoWheeler from '@mui/icons-material/TwoWheeler';
import { useCustomerContext } from '../../../components/CustomerProvider.tsx';
import {
    formatZoneName,
    ParkingaboProductTemplate,
} from './ParkingaboProductTemplateModels.ts';
import { ZoneType } from 'dg-web-shared/dto/ZoneType.ts';
import { ParkingaboProductType } from 'product-shared/product-template/ParkingaboProductType.ts';

export function ProductsConfigurationOutlet() {
    const { refetchProducts } = useCustomerContext();
    const { zones, productSelectionState, disableProductChange } =
        useProductAddContext();
    if (productSelectionState.product === null) {
        return null;
    }
    if (
        productSelectionState.product?.type ===
        ParkingaboProductType.RESERVATION
    ) {
        return (
            <ProductsConfigurationReservation
                product={productSelectionState.product}
                zones={zones}
                noProductChange={disableProductChange}
                refetchProducts={refetchProducts}
            />
        );
    }

    if (
        productSelectionState.product?.type ===
        ParkingaboProductType.SUBSCRIPTION
    ) {
        return (
            <ProductsConfigurationSubscription
                product={productSelectionState.product}
                zones={zones}
                noProductChange={disableProductChange}
                refetchProducts={refetchProducts}
            />
        );
    }

    if (
        productSelectionState.product?.type ===
        ParkingaboProductType.GUEST_RESERVATION_UBS
    ) {
        return (
            <ProductsConfigurationGuestReservation
                product={productSelectionState.product}
                zones={zones}
                noProductChange={disableProductChange}
                refetchProducts={refetchProducts}
            />
        );
    }
    exhaustiveGuard(productSelectionState.product);
}

export function VehicleTypes({
    vehicleTypes,
}: {
    vehicleTypes: VehicleLicensePlateType[];
}) {
    return (
        <ParkingaboOverlineList.Item
            label={
                vehicleTypes.length <= 1 ? (
                    <Localized
                        de="Fahrzeugtyp"
                        fr="Type de véhicule"
                        it="Tipo di veicolo"
                        en="Vehicle type"
                    />
                ) : (
                    <Localized
                        de="Fahrzeugtypen"
                        fr="Types de véhicules"
                        it="Tipi di veicolo"
                        en="Vehicle types"
                    />
                )
            }
        >
            {vehicleTypes.map(vehicleType => (
                <Stack
                    key={vehicleType}
                    direction={'row'}
                    spacing={2}
                    sx={{
                        justifyItems: 'flex-start',
                    }}
                >
                    {vehicleType === VehicleLicensePlateType.CAR ? (
                        <>
                            <div>
                                <Localized
                                    de="Auto"
                                    fr="Voiture"
                                    it="Automobile"
                                    en="Car"
                                />
                            </div>
                            <DriveEta fontSize="small" />
                        </>
                    ) : (
                        <>
                            <div>
                                <Localized
                                    de="Motorrad"
                                    fr="Moto"
                                    it="Motocicletta"
                                    en="Motorcycle"
                                />
                            </div>
                            <TwoWheeler fontSize="small" />
                        </>
                    )}
                </Stack>
            ))}
        </ParkingaboOverlineList.Item>
    );
}

export function ProductZoneInfo({
    product,
    zones,
}: {
    product: ParkingaboProductTemplate;
    zones: ParkingaboZoneInfo[];
}) {
    const productZones = zones.filter(zone =>
        product.zoneIds.includes(zone.zoneId),
    );
    return (
        <ParkingaboOverlineList.Item
            label={<ProductZoneInfoTitle productZones={productZones} />}
        >
            {productZones.map(zone => (
                <Box
                    sx={{
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                    }}
                    key={zone.zoneId}
                >
                    {formatZoneName(zone)}
                </Box>
            ))}
        </ParkingaboOverlineList.Item>
    );
}

export function ProductZoneInfoTitle({
    productZones,
}: {
    productZones: ParkingaboZoneInfo[];
}) {
    if (productZones[0].zoneType === ZoneType.enforced) {
        if (productZones.length > 1) {
            return <Localized de="Zonen" fr="Zones" it="Zone" en="Zones" />;
        }
        return <Localized de="Zone" fr="Zone" it="Zona" en="Zone" />;
    }
    if (productZones.length > 1) {
        return (
            <Localized
                de="Parkings"
                fr="Parkings"
                it="Parcheggi"
                en="Parkings"
            />
        );
    }
    return <Localized de="Parking" fr="Parking" it="Parcheggio" en="Parking" />;
}
